import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';

import CurrenciesContext from 'hb-react/frontoffice/contexts/CurrenciesContext';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import {
  Number,
  Select,
} from 'hb-react/shared/components/formik/components/inputs';
import { IsActive } from 'hb-react/shared/components/IsActive';

import { type Fields, type FormValues } from '../types';

export const Salary = ({ fields }: { fields?: Fields }) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const options = [
    fields?.yearly_salary && {
      id: 'yearly_salary',
      name: s__('JobForm|Yearly salary (gross)'),
    },
    fields?.monthly_salary && {
      id: 'monthly_salary',
      name: s__('JobForm|Monthly salary (gross)'),
    },
    fields?.daily_salary && {
      id: 'daily_salary',
      name: s__('JobForm|Daily salary (gross)'),
    },
  ].filter(Boolean);

  const interval = [
    ...[
      values.yearly_salary && 'yearly_salary',
      values.monthly_salary && 'monthly_salary',
      values.daily_salary && 'daily_salary',
    ].filter(Boolean),
    null,
  ][0];

  useEffect(() => {
    setFieldValue('pay_period', interval);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const width = useMemo(
    () => (options.length === 1 || !values.pay_period ? 1 / 2 : 1 / 3),
    [options, values.pay_period],
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <Col width={{ _: 1, m: 0.5 }}>
      <Grid mx={0}>
        <Row>
          <Col width={width}>
            <CurrenciesContext.Consumer>
              {(currencies = []) => {
                const opts = currencies.map(
                  ({ id, iso_code: isoCode, symbol }) => ({
                    id,
                    name: [symbol, isoCode].join(' - '),
                  }),
                );

                return (
                  <Select
                    label={s__('JobForm|Currency')}
                    name="currency"
                    options={opts}
                    validate={{ required: Boolean(values?.pay_period) }}
                  />
                );
              }}
            </CurrenciesContext.Consumer>
          </Col>
          <>
            {options.length > 1 && (
              <Col width={width}>
                <Select
                  label={s__('JobForm|Pay period')}
                  name="pay_period"
                  options={options}
                />
              </Col>
            )}
          </>
          <IsActive isActive={Boolean(values.pay_period)}>
            <Col width={width}>
              <IsActive isActive={values.pay_period === 'yearly_salary'}>
                <Number
                  label={s__('JobForm|Yearly salary (gross)')}
                  name="yearly_salary"
                  validate={{ required: values.pay_period === 'yearly_salary' }}
                />
              </IsActive>
              <IsActive isActive={values.pay_period === 'monthly_salary'}>
                <Number
                  label={s__('JobForm|Monthly salary (gross)')}
                  name="monthly_salary"
                  validate={{
                    required: values.pay_period === 'monthly_salary',
                  }}
                />
              </IsActive>
              <IsActive isActive={values.pay_period === 'daily_salary'}>
                <Number
                  label={s__('JobForm|Daily salary (gross)')}
                  name="daily_salary"
                  validate={{ required: values.pay_period === 'daily_salary' }}
                />
              </IsActive>
            </Col>
          </IsActive>
        </Row>
      </Grid>
    </Col>
  );
};
